import React, { useState, useRef } from 'react';
import { 
  Sparkles, 
  Award, 
  CheckCircle2, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  ShieldCheck, 
  Check, 
  Cloud,
  Mail,
  FileText,
  Copy,
  Lock,
  Building2,
  GraduationCap,
  User,
  ArrowRight,
  QrCode
} from 'lucide-react';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const TRACKS = [
  {
    id: 'cloud-devops',
    category: 'cloud',
    title: 'Cloud Computing, DevOps & Infrastructure Architecture',
    badge: 'Cloud & Infrastructure',
    badgeColor: '#0284c7',
    badgeBg: '#f0f9ff',
    duration: '15 Days / 4 to 12 Weeks',
    level: 'Beginner to Advanced',
    desc: 'Architect scalable, resilient cloud infrastructure. Master Amazon Web Services (AWS) and Google Cloud Platform (GCP), containerization with Docker, Kubernetes orchestration, CI/CD automated deployment pipelines with GitHub Actions, Linux administration, and secure cloud networking.',
    skills: ['AWS (EC2, S3, Lambda, IAM)', 'Google Cloud (GCP / Cloud Run)', 'Docker & Containerization', 'Kubernetes (K8s)', 'CI/CD & GitHub Actions', 'Linux & Bash Shell', 'Cloud Security & VPC'],
    deliverable: 'Production-ready containerized microservice deployed on AWS/GCP with automated CI/CD pipeline, SSL/TLS security, and monitoring dashboard.'
  },
  {
    id: 'ai-edtech',
    category: 'ai',
    title: 'Generative AI, LLMs & Multimodal EdTech',
    badge: 'AI & Engineering',
    badgeColor: '#059669',
    badgeBg: '#ecfdf5',
    duration: '15 Days / 4 to 12 Weeks',
    level: 'Intermediate to Advanced',
    desc: 'Engineer next-generation conversational agents, multimodal reasoning pipelines with Google Gemini 1.5/2.0 API, speech-to-text pronunciation analysis, and full-stack React/FastAPI architectures.',
    skills: ['Python 3.12+', 'FastAPI', 'React.js', 'Google Gemini API', 'Prompt Engineering', 'Audio Signal Processing', 'REST APIs'],
    deliverable: 'Functional Multimodal AI EdTech Module deployed on live cloud infrastructure with real-time user testing.'
  },
  {
    id: 'fullstack-web',
    category: 'software',
    title: 'Full-Stack Web Applications & Real-Time APIs',
    badge: 'Full-Stack Software',
    badgeColor: '#4f46e5',
    badgeBg: '#eef2ff',
    duration: '15 Days / 4 to 12 Weeks',
    level: 'Beginner to Intermediate',
    desc: 'Build secure, responsive, enterprise-grade web applications. Master React component architecture, Node.js/Python backends, relational and NoSQL databases, RESTful & WebSocket communications, JWT authentication, and automated testing suites.',
    skills: ['React 18 / Next.js', 'Node.js / Express', 'PostgreSQL / MongoDB', 'REST & GraphQL', 'State Management', 'Tailwind CSS', 'Git & Agile Sprint'],
    deliverable: 'End-to-end full-stack web application with responsive UI, authenticated user dashboard, and automated unit test coverage.'
  },
  {
    id: 'data-science',
    category: 'ai',
    title: 'Data Science, Advanced Analytics & ML Engineering',
    badge: 'Data & Analytics',
    badgeColor: '#b45309',
    badgeBg: '#fffbeb',
    duration: '15 Days / 4 to 12 Weeks',
    level: 'Intermediate',
    desc: 'Analyze complex real-world datasets, engineer predictive machine learning pipelines, conduct statistical hypothesis testing, and build interactive analytical dashboards using Python, Pandas, Scikit-learn, and Streamlit.',
    skills: ['Python & Pandas', 'Data Wrangling & Cleaning', 'Scikit-learn', 'Exploratory Data Analysis', 'Data Visualization', 'Statistical Inference', 'Streamlit Apps'],
    deliverable: 'Comprehensive exploratory analytics report, trained machine learning model with performance benchmarks, and interactive dashboard.'
  },
  {
    id: 'applied-linguistics',
    category: 'linguistics',
    title: 'Applied Linguistics, Natural Language & Communicative English Research',
    badge: 'Humanities & EdTech',
    badgeColor: '#9333ea',
    badgeBg: '#faf5ff',
    duration: '15 Days / 4 to 12 Weeks',
    level: 'All Academic Levels',
    desc: 'Conduct empirical research at the intersection of English language teaching (ELT), second-language phonetics, pedagogical scaffolding, and AI-assisted vocabulary acquisition. Co-author academic papers with senior research advisors.',
    skills: ['Corpus Linguistics', 'Phonetics & Pronunciation', 'Pedagogical Frameworks', 'Qualitative Research', 'Academic Writing', 'SPSS / Statistical Tools', 'Curriculum Design'],
    deliverable: 'Co-authored scholarly research paper draft formatted for peer-reviewed journal submission with empirical classroom methodology.'
  },
  {
    id: 'cybersecurity',
    category: 'software',
    title: 'Cybersecurity, Network Defense & Incident Response',
    badge: 'Security & Systems',
    badgeColor: '#dc2626',
    badgeBg: '#fef2f2',
    duration: '15 Days / 4 to 12 Weeks',
    level: 'Intermediate to Advanced',
    desc: 'Perform vulnerability assessments, understand penetration testing workflows, secure cloud and web architectures against OWASP Top 10 vulnerabilities, analyze network packet traces with Wireshark, and implement security information and event management (SIEM).',
    skills: ['Network Security', 'OWASP Top 10', 'Vulnerability Assessment', 'Wireshark & Packet Analysis', 'Linux Hardening', 'Cryptography & PKI', 'Security Auditing'],
    deliverable: 'Comprehensive vulnerability assessment and penetration testing (VAPT) audit report with remediated code snippets and defensive configurations.'
  }
];

const FAQS = [
  {
    q: 'Are these internships recognized for official University / College academic credits?',
    a: 'Yes. Arivu Nest Research and Development is an officially registered MSME Government of India research unit (UDYAM-TN-32-0048110) operating in academic association with Kani Rathnam Equality Foundation. Every scholar receives a verifiable 10-year digital appointment & completion credential accessible on arivunest.com/verify, compliant with AICTE and university internship rubrics.'
  },
  {
    q: 'Can I complete this internship completely online while attending regular college classes?',
    a: 'Yes! 100% of the project cohorts are conducted virtually with flexible, asynchronous sprint schedules. Weekly mentor syncs and milestone evaluations are conducted online, allowing students across all Indian universities to participate seamlessly without disrupting regular college coursework.'
  },
  {
    q: 'What is the Subsidized Grant of ₹299 used for?',
    a: 'The nominal processing grant of ₹299 covers cloud project sandboxes, 1-on-1 mentor code reviews, issuance of the official MSME Government of India-recognized certificate, 10-year online credential verification hosting at arivunest.com/verify, and digital executive scholar ID card generation.'
  },
  {
    q: 'Do interns receive a formal Letter of Recommendation (LOR)?',
    a: 'Scholars who achieve exemplary evaluations and complete their project deliverables receive a formal, personalized Letter of Recommendation (LOR) on official institutional letterhead signed by Founder & Executive Director Dr. S. Sabitha Shunmugapriya, Ph.D, suitable for higher studies (MS/Ph.D) and top MNC placements.'
  },
  {
    q: 'How does the Faculty Referral ID work?',
    a: 'If you were guided to this program by an Arivu Nest Advisory Council Member (such as Dr. K. Vinoth from SRMIST or Prof. A. Dinesh Kumar from Sri Ranganathar Institute), entering their Reference ID directly credits them in our institutional academic race and fast-tracks your mentor allocation.'
  }
];

const Internships = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openFaq, setOpenFaq] = useState(null);
  const formRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    college: '',
    collegeCity: '',
    degree: '',
    yearOfStudy: '3rd Year Undergraduate',
    track: 'Cloud Computing, DevOps & Infrastructure Architecture',
    duration: '15 Days (Accelerated Winter Sprint — ₹299)',
    mode: 'Virtual (Work from Home with Mentor Sync)',
    referralId: '',
    githubUrl: '',
    utrNumber: '',
    agreeTerms: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [submittedApp, setSubmittedApp] = useState(null);
  const [copiedAppId, setCopiedAppId] = useState(false);

  const filteredTracks = selectedCategory === 'all' 
    ? TRACKS 
    : TRACKS.filter(t => t.category === selectedCategory);

  const handleTrackSelect = (trackTitle) => {
    setFormData(prev => ({ ...prev, track: trackTitle }));
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToApply = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const generateAppId = () => {
    const saved = localStorage.getItem('arivunest_internship_apps');
    let count = 2; // Member 1 was Prabakar A (ANRD/INT/2026/001)
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        count = 2 + parsed.length;
      } catch (e) {}
    }
    const seq = String(count).padStart(3, '0');
    return `ANRD/INT/2026/${seq}`;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.college.trim()) {
      alert('Please fill in your Name, Email, WhatsApp Phone Number, and College Name.');
      return;
    }

    if (!formData.utrNumber.trim()) {
      alert('Please enter your 12-digit UPI Transaction ID / UTR Number for payment confirmation.');
      return;
    }

    if (!formData.agreeTerms) {
      alert('Please confirm the institutional declaration by checking the declaration box.');
      return;
    }

    setSubmitting(true);
    const appId = generateAppId();

    const appRecord = {
      appId,
      ...formData,
      submittedAt: new Date().toISOString(),
      status: 'PROVISIONAL_CONFIRMED'
    };

    // 1. Save to LocalStorage Backup
    try {
      const existing = JSON.parse(localStorage.getItem('arivunest_internship_apps') || '[]');
      existing.push(appRecord);
      localStorage.setItem('arivunest_internship_apps', JSON.stringify(existing));
    } catch (err) {
      console.warn('LocalStorage save error:', err);
    }

    // 2. Save to Firestore
    try {
      await addDoc(collection(db, 'internship_applications'), {
        ...appRecord,
        serverTimestamp: serverTimestamp()
      });
    } catch (err) {
      console.warn('Firestore write handled gracefully:', err);
    }

    setSubmittedApp(appRecord);
    setSubmitting(false);

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCopyAppId = () => {
    if (submittedApp && submittedApp.appId) {
      navigator.clipboard.writeText(submittedApp.appId);
      setCopiedAppId(true);
      setTimeout(() => setCopiedAppId(false), 2500);
    }
  };

  return (
    <div style={styles.page}>
      
      {/* Hero Header */}
      <section style={styles.heroSection}>
        <div style={styles.heroContainer}>
          <div style={styles.heroBadge}>
            <Sparkles size={14} style={{ marginRight: '6px' }} />
            GOVT. MSME REGISTERED &bull; UDYAM-TN-32-0048110 &bull; 2026 ADMISSIONS OPEN
          </div>
          
          <h1 style={styles.heroTitle}>
            Student Internships &amp; Research Incubation
          </h1>
          
          <p style={styles.heroSubtitle}>
            Bridge collegiate academia with frontier Cloud Computing, DevOps infrastructure, Generative AI engineering, full-stack architectures, and academic research paper co-authorship with our 50+ Ph.D. Advisory Council.
          </p>

          {/* Quick Institutional Trust Metrics */}
          <div style={styles.heroMetricsGrid}>
            <div style={styles.metricCard}>
              <div style={styles.metricIconBox}><ShieldCheck size={18} color="#059669" /></div>
              <div>
                <div style={styles.metricValue}>Govt. MSME Recognized</div>
                <div style={styles.metricLabel}>UDYAM-TN-32-0048110</div>
              </div>
            </div>

            <div style={styles.metricCard}>
              <div style={styles.metricIconBox}><Building2 size={18} color="#0284c7" /></div>
              <div>
                <div style={styles.metricValue}>50+ Ph.D Advisory Faculty</div>
                <div style={styles.metricLabel}>SRMIST, Kongunadu, Sri Ranganathar</div>
              </div>
            </div>

            <div style={styles.metricCard}>
              <div style={styles.metricIconBox}><Cloud size={18} color="#4f46e5" /></div>
              <div>
                <div style={styles.metricValue}>100% Virtual Project Sprints</div>
                <div style={styles.metricLabel}>Flexible Daily Async Mentorship</div>
              </div>
            </div>

            <div style={styles.metricCard}>
              <div style={styles.metricIconBox}><Award size={18} color="#059669" /></div>
              <div>
                <div style={styles.metricValue}>Formal LOR &amp; Verification</div>
                <div style={styles.metricLabel}>Live at arivunest.com/verify</div>
              </div>
            </div>
          </div>

          {/* Hero Action Buttons */}
          <div style={styles.heroCtaWrap}>
            <button 
              onClick={scrollToApply}
              style={styles.heroPrimaryBtn}
            >
              <FileText size={16} style={{ marginRight: '8px' }} />
              Apply for 2026 Project Cohort &darr;
            </button>
            <a 
              href="/verify"
              style={styles.heroSecondaryBtn}
            >
              <ShieldCheck size={15} style={{ marginRight: '8px' }} />
              Verify Institutional Credentials
            </a>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div style={styles.contentContainer}>
        
        {/* Cohort Intake Status Banner */}
        <div style={styles.intakeBanner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={styles.livePulseDot}></span>
            <div>
              <strong style={{ color: '#065f46', fontSize: '15px' }}>Current Cohort Intake: Winter 2026 Academic Sprint</strong>
              <div style={{ color: '#047857', fontSize: '13px', marginTop: '2px' }}>
                Direct Admissions Active &bull; Limited to 25 Scholars per Track &bull; 15-Day, 4-Week, or 12-Week Tracks
              </div>
            </div>
          </div>
          <button 
            onClick={scrollToApply}
            style={styles.fastTrackPill}
          >
            Apply Directly Below &darr;
          </button>
        </div>

        {/* Track Explorer Header & Filter Tabs */}
        <div style={{ marginTop: '40px', marginBottom: '24px' }}>
          <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 28px auto' }}>
            <span style={styles.sectionPill}>Specialized Domains</span>
            <h2 style={styles.sectionHeading}>Choose Your Research &amp; Engineering Track</h2>
            <p style={styles.sectionSub}>
              Curated, production-aligned capstone roadmaps designed in collaboration with senior university faculty and industry architects.
            </p>
          </div>

          <div style={styles.filterTabsWrap}>
            {[
              { id: 'all', label: 'All Specializations (6)' },
              { id: 'cloud', label: 'Cloud & Infrastructure' },
              { id: 'ai', label: 'AI, LLMs & Analytics' },
              { id: 'software', label: 'Software & Security' },
              { id: 'linguistics', label: 'Applied Linguistics & ELT' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setSelectedCategory(tab.id)}
                style={selectedCategory === tab.id ? styles.filterTabActive : styles.filterTab}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tracks Grid */}
        <div style={styles.tracksGrid}>
          {filteredTracks.map(track => (
            <div key={track.id} style={styles.trackCard}>
              <div style={styles.trackCardHeader}>
                <span style={{ ...styles.trackBadge, background: track.badgeBg, color: track.badgeColor, borderColor: track.badgeColor + '30' }}>
                  {track.badge}
                </span>
                <span style={styles.trackDuration}>
                  <Clock size={13} style={{ marginRight: '4px' }} />
                  {track.duration}
                </span>
              </div>

              <h3 style={styles.trackTitle}>{track.title}</h3>
              <p style={styles.trackDesc}>{track.desc}</p>

              <div style={{ marginTop: '16px' }}>
                <div style={styles.trackSubheading}>Key Competencies &amp; Toolchains:</div>
                <div style={styles.skillsWrap}>
                  {track.skills.map((skill, sIdx) => (
                    <span key={sIdx} style={styles.skillTag}>{skill}</span>
                  ))}
                </div>
              </div>

              <div style={styles.deliverableBox}>
                <strong style={{ color: '#0f1e41', fontSize: '12.5px', display: 'block', marginBottom: '4px' }}>
                  Capstone Deliverable:
                </strong>
                <span style={{ color: '#475569', fontSize: '13px', lineHeight: '1.5' }}>
                  {track.deliverable}
                </span>
              </div>

              <button
                onClick={() => handleTrackSelect(track.title)}
                style={styles.trackApplyBtn}
              >
                Apply for this Track &rarr;
              </button>
            </div>
          ))}
        </div>

        {/* ============================================================== */}
        {/* NATIVE DIRECT APPLICATION PORTAL (NO EXTERNAL GOOGLE FORMS) */}
        {/* ============================================================== */}
        <section ref={formRef} id="apply-portal" style={styles.formSection}>
          <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 32px auto' }}>
            <span style={styles.sectionPill}>Official Portal</span>
            <h2 style={styles.sectionHeading}>Official Candidate Application Portal</h2>
            <p style={styles.sectionSub}>
              Register directly on our institutional domain. Immediate provisional admission confirmation, candidate reference ID generation, and supervisor allocation.
            </p>
          </div>

          {submittedApp ? (
            /* Confirmation Receipt / Admission Slip View */
            <div style={styles.successBox}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#ecfdf5', border: '3px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
                <CheckCircle2 size={38} color="#059669" />
              </div>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '24px', color: '#065f46', fontWeight: '800' }}>
                Provisional Admission Confirmed!
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '14.5px', color: '#047857' }}>
                Welcome to Arivu Nest Research and Development. Your candidate file has been recorded.
              </p>

              <div style={styles.appIdDisplay}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', fontWeight: '700', marginBottom: '4px' }}>
                  Official Candidate Reference ID
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: '800', color: '#0f1e41' }}>
                    {submittedApp.appId}
                  </span>
                  <button onClick={handleCopyAppId} style={styles.copyBtn}>
                    {copiedAppId ? <Check size={16} color="#059669" /> : <Copy size={16} />}
                    {copiedAppId ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Admission Summary Table */}
              <div style={styles.receiptCard}>
                <div style={styles.receiptRow}>
                  <span style={styles.receiptLabel}>Candidate Name:</span>
                  <span style={styles.receiptVal}>{submittedApp.fullName}</span>
                </div>
                <div style={styles.receiptRow}>
                  <span style={styles.receiptLabel}>Assigned Track:</span>
                  <span style={styles.receiptVal}>{submittedApp.track}</span>
                </div>
                <div style={styles.receiptRow}>
                  <span style={styles.receiptLabel}>Institution:</span>
                  <span style={styles.receiptVal}>{submittedApp.college}</span>
                </div>
                <div style={styles.receiptRow}>
                  <span style={styles.receiptLabel}>Duration:</span>
                  <span style={styles.receiptVal}>{submittedApp.duration}</span>
                </div>
                <div style={styles.receiptRow}>
                  <span style={styles.receiptLabel}>Transaction UTR:</span>
                  <span style={{ ...styles.receiptVal, fontFamily: 'monospace', color: '#047857' }}>{submittedApp.utrNumber}</span>
                </div>
                <div style={{ ...styles.receiptRow, borderBottom: 'none' }}>
                  <span style={styles.receiptLabel}>Accreditation Unit:</span>
                  <span style={styles.receiptVal}>MSME Govt. of India (UDYAM-TN-32-0048110)</span>
                </div>
              </div>

              <div style={{ margin: '24px 0', fontSize: '13.5px', color: '#475569', lineHeight: '1.6', textAlign: 'center', maxWidth: '580px', marginInline: 'auto' }}>
                <strong>Next Steps:</strong> Join the official Scholar WhatsApp Group below to meet your mentors. Your formal <strong>Appointment Letter &amp; GitHub Project Access</strong> will be dispatched to <strong>{submittedApp.email}</strong> within 24 hours.
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a 
                  href="https://chat.whatsapp.com/E79PDHbC0n286T9ChYsgXo" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={styles.whatsappJoinBtn}
                >
                  Join Official Scholar WhatsApp Community &rarr;
                </a>
                <button 
                  onClick={() => window.print()}
                  style={styles.printBtn}
                >
                  Print / Save Admission Slip
                </button>
              </div>

              <div style={{ marginTop: '24px' }}>
                <button 
                  onClick={() => {
                    setSubmittedApp(null);
                    setFormData({
                      fullName: '',
                      email: '',
                      phone: '',
                      college: '',
                      collegeCity: '',
                      degree: '',
                      yearOfStudy: '3rd Year Undergraduate',
                      track: 'Cloud Computing, DevOps & Infrastructure Architecture',
                      duration: '15 Days (Accelerated Winter Sprint — ₹299)',
                      mode: 'Virtual (Work from Home with Mentor Sync)',
                      referralId: '',
                      githubUrl: '',
                      utrNumber: '',
                      agreeTerms: false
                    });
                  }}
                  style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Submit another candidate application
                </button>
              </div>
            </div>
          ) : (
            /* The Native Direct Application Form */
            <form onSubmit={handleFormSubmit} style={styles.formCard}>
              
              {/* Institutional Header Banner inside Form */}
              <div style={styles.formBannerTop}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ShieldCheck size={24} color="#047857" />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '17px', color: '#0f1e41', fontWeight: '800' }}>
                      Arivu Nest R&amp;D Admissions &bull; Winter 2026 Cohort
                    </h3>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      MSME Govt. of India (UDYAM-TN-32-0048110) &bull; Verified Online Credential
                    </p>
                  </div>
                </div>
              </div>

              <div style={styles.formGrid}>
                {/* Full Legal Name */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <User size={13} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                    Full Legal Name (as per College ID) *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="e.g., S. Vignesh / K. Deepika"
                    style={styles.formInput}
                    required
                  />
                </div>

                {/* Email Address */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <Mail size={13} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                    Email Address (for Offer Letter &amp; Credential) *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="vignesh@gmail.com"
                    style={styles.formInput}
                    required
                  />
                </div>

                {/* WhatsApp Number */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    WhatsApp Contact Number (for Mentor Cohort) *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 98765 43210"
                    style={styles.formInput}
                    required
                  />
                </div>

                {/* College / Institution */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <Building2 size={13} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                    College / University Name *
                  </label>
                  <input
                    type="text"
                    name="college"
                    value={formData.college}
                    onChange={handleInputChange}
                    placeholder="e.g., KGiSL Institute of Tech / SRMIST / Kongunadu"
                    style={styles.formInput}
                    required
                  />
                </div>

                {/* College City & State */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>College Location (City, State) *</label>
                  <input
                    type="text"
                    name="collegeCity"
                    value={formData.collegeCity}
                    onChange={handleInputChange}
                    placeholder="e.g., Coimbatore, Tamil Nadu"
                    style={styles.formInput}
                    required
                  />
                </div>

                {/* Degree & Department */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    <GraduationCap size={13} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                    Degree &amp; Department / Branch *
                  </label>
                  <input
                    type="text"
                    name="degree"
                    value={formData.degree}
                    onChange={handleInputChange}
                    placeholder="e.g., B.Tech AI&DS / B.E. CSE / B.Sc / M.A."
                    style={styles.formInput}
                    required
                  />
                </div>

                {/* Year of Study */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Current Year of Study *</label>
                  <select
                    name="yearOfStudy"
                    value={formData.yearOfStudy}
                    onChange={handleInputChange}
                    style={styles.formSelect}
                  >
                    <option value="1st Year Undergraduate">1st Year Undergraduate</option>
                    <option value="2nd Year Undergraduate">2nd Year Undergraduate</option>
                    <option value="3rd Year Undergraduate">3rd Year Undergraduate</option>
                    <option value="Final Year (4th Year)">Final Year (4th Year)</option>
                    <option value="Postgraduate (M.Tech / M.A. / MCA)">Postgraduate (M.Tech / M.A. / MCA)</option>
                    <option value="Research Scholar / Recent Graduate">Research Scholar / Recent Graduate</option>
                  </select>
                </div>

                {/* Selected Track */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Preferred Internship Track *</label>
                  <select
                    name="track"
                    value={formData.track}
                    onChange={handleInputChange}
                    style={styles.formSelect}
                  >
                    {TRACKS.map(t => (
                      <option key={t.id} value={t.title}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Preferred Duration */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Preferred Sprint Duration &amp; Processing Grant *</label>
                  <select
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    style={styles.formSelect}
                  >
                    <option value="15 Days (Accelerated Winter Sprint — ₹299)">15 Days (Accelerated Winter Sprint — ₹299)</option>
                    <option value="4 Weeks (1-Month Standard Internship — ₹499)">4 Weeks (1-Month Standard Internship — ₹499)</option>
                    <option value="12 Weeks (Semester University Credit Track — ₹999)">12 Weeks (Semester University Credit Track — ₹999)</option>
                    <option value="24 Weeks (Capstone & Scholarly Publication — ₹1,499)">24 Weeks (Capstone &amp; Scholarly Publication — ₹1,499)</option>
                  </select>
                </div>

                {/* Mode */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Internship Mode</label>
                  <select
                    name="mode"
                    value={formData.mode}
                    onChange={handleInputChange}
                    style={styles.formSelect}
                  >
                    <option value="Virtual (Work from Home with Mentor Sync)">Virtual (Work from Home with Online Mentor Sync)</option>
                    <option value="Hybrid (Virtual + Campus Hub Sync)">Hybrid (Virtual + Campus Hub Sync)</option>
                  </select>
                </div>

                {/* Faculty Referral ID */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Faculty / Advisory Member Referral ID (Optional)
                  </label>
                  <input
                    type="text"
                    name="referralId"
                    value={formData.referralId}
                    onChange={handleInputChange}
                    placeholder="e.g., ANRD/AB/2026/018 or ANRD/AB/2026/020"
                    style={styles.formInput}
                  />
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>
                    Credits your college professor in the official Arivu Nest Leadership Race.
                  </div>
                </div>

                {/* GitHub / LinkedIn Profile */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>GitHub / LinkedIn / Portfolio Link (Optional)</label>
                  <input
                    type="url"
                    name="githubUrl"
                    value={formData.githubUrl}
                    onChange={handleInputChange}
                    placeholder="https://github.com/your-username"
                    style={styles.formInput}
                  />
                </div>
              </div>

              {/* ============================================================== */}
              {/* Subsidized Grant & Official UPI Payment Verification Card */}
              {/* ============================================================== */}
              <div style={styles.paymentSectionBox}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <QrCode size={20} color="#047857" />
                    <strong style={{ fontSize: '16px', color: '#0f1e41' }}>
                      Subsidized Project Kit &amp; MSME Certification Grant: ₹299
                    </strong>
                  </div>
                  <span style={styles.govtApprovedBadge}>
                    MSME Govt. of India Approved
                  </span>
                </div>

                <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                  This nominal grant subsidizes your cloud sandbox environments, 1-on-1 mentor evaluations, verifiable 10-year credential registry at <code>arivunest.com/verify</code>, and Letter of Recommendation (LOR) processing.
                </p>

                {/* Deliverables Checklist */}
                <div style={styles.deliverablesList}>
                  <div style={styles.delivItem}><Check size={14} color="#059669" /> Govt. MSME Recognized Certificate</div>
                  <div style={styles.delivItem}><Check size={14} color="#059669" /> 24/7 Online Credential Verification</div>
                  <div style={styles.delivItem}><Check size={14} color="#059669" /> Live GitHub Production Capstone</div>
                  <div style={styles.delivItem}><Check size={14} color="#059669" /> Official Digital Scholar ID Card</div>
                </div>

                {/* Payment Gateway Box */}
                <div style={styles.upiInfoCard}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f1e41', marginBottom: '6px' }}>
                    Official Payment Details (Google Pay / PhonePe / Paytm / BHIM UPI):
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={styles.upiPill}>
                      UPI ID: <strong style={{ color: '#047857', marginLeft: '4px' }}>nandhakumar@arivunest.com</strong>
                    </div>
                    <div style={styles.upiPill}>
                      Beneficiary: <strong style={{ color: '#0f1e41', marginLeft: '4px' }}>Arivu Nest R&amp;D (Jjegan Graphers)</strong>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                    Transfer ₹299 from any UPI app (GPay / PhonePe / Paytm) and enter the 12-digit UPI UTR / Transaction ID below:
                  </div>
                </div>

                {/* UTR Input Field */}
                <div style={{ marginTop: '16px' }}>
                  <label style={styles.formLabel}>
                    UPI Transaction ID / UTR Number (12 Digits) *
                  </label>
                  <input
                    type="text"
                    name="utrNumber"
                    value={formData.utrNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., 630399448109 or T261031..."
                    style={{ ...styles.formInput, borderColor: '#059669', fontFamily: 'monospace', fontWeight: '700' }}
                    required
                  />
                  <div style={{ fontSize: '11.5px', color: '#047857', marginTop: '4px' }}>
                    Found in your payment app under "UPI Transaction ID" or "UTR". Used for instant automated verification.
                  </div>
                </div>
              </div>

              {/* Declaration Checkbox */}
              <div style={{ marginTop: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  style={{ marginTop: '3px', cursor: 'pointer', width: '16px', height: '16px', accentColor: '#059669' }}
                  required
                />
                <label htmlFor="agreeTerms" style={{ fontSize: '12.5px', color: '#475569', lineHeight: '1.5', cursor: 'pointer' }}>
                  I confirm that all submitted academic details are authentic. I agree to abide by the academic integrity and project delivery standards of Arivu Nest Research and Development.
                </label>
              </div>

              {/* Submit Button */}
              <div style={{ marginTop: '26px', textAlign: 'center' }}>
                <button
                  type="submit"
                  disabled={submitting}
                  style={submitting ? styles.submitBtnDisabled : styles.submitBtn}
                >
                  {submitting ? 'Verifying & Registering...' : 'Submit Application & Generate Official Candidate ID →'}
                </button>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '10px' }}>
                  🔒 Official National Portal &bull; Encrypted &bull; Direct Supervisor Allocation
                </div>
              </div>
            </form>
          )}
        </section>

        {/* Frequently Asked Questions */}
        <section style={{ marginTop: '64px', marginBottom: '56px' }}>
          <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 36px auto' }}>
            <span style={styles.sectionPill}>Clarifications</span>
            <h2 style={styles.sectionHeading}>Frequently Asked Questions</h2>
            <p style={styles.sectionSub}>
              Everything you need to know about university approvals, schedules, and evaluation standards.
            </p>
          </div>

          <div style={{ maxWidth: '780px', margin: '0 auto' }}>
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={idx} style={styles.faqCard}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    style={styles.faqHeader}
                  >
                    <span style={styles.faqQuestion}>{faq.q}</span>
                    {isOpen ? <ChevronUp size={18} color="#059669" /> : <ChevronDown size={18} color="#64748b" />}
                  </button>
                  {isOpen && (
                    <div style={styles.faqAnswer}>
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Student Helpdesk Callout */}
        <div style={styles.helpdeskBanner}>
          <div>
            <h3 style={{ margin: '0 0 6px 0', fontSize: '18px', color: '#0f1e41', fontWeight: '800' }}>
              Have Questions Before Registering?
            </h3>
            <p style={{ margin: 0, fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
              Connect directly with our Student Coordination Secretariat for inquiries regarding curriculum tracks, university credit mapping, or custom cohort schedules.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={scrollToApply}
              style={styles.helpdeskGFormBtn}
            >
              <FileText size={15} style={{ marginRight: '6px' }} />
              Open Application Portal
            </button>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=nandhakumar@arivunest.com&su=Student%20Internship%20Inquiry%20-%20Arivu%20Nest"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.helpdeskEmailBtn}
            >
              <Mail size={15} style={{ marginRight: '6px' }} />
              Email Secretariat
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    backgroundColor: '#ffffff',
    color: '#0f172a',
    minHeight: '100vh',
    paddingBottom: '80px'
  },
  heroSection: {
    background: 'linear-gradient(180deg, #0f1e41 0%, #1e293b 100%)',
    color: '#ffffff',
    padding: '80px 24px 60px 24px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  heroContainer: {
    maxWidth: '960px',
    margin: '0 auto'
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'rgba(5, 150, 105, 0.2)',
    color: '#34d399',
    border: '1px solid rgba(52, 211, 153, 0.3)',
    borderRadius: '100px',
    padding: '6px 16px',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    marginBottom: '20px'
  },
  heroTitle: {
    fontSize: '42px',
    fontWeight: '900',
    color: '#ffffff',
    margin: '0 0 16px 0',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  },
  heroSubtitle: {
    fontSize: '17px',
    color: '#cbd5e1',
    lineHeight: '1.7',
    maxWidth: '780px',
    margin: '0 auto 36px auto'
  },
  heroMetricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: '16px',
    marginBottom: '36px'
  },
  metricCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '14px 16px',
    textAlign: 'left'
  },
  metricIconBox: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  metricValue: {
    fontSize: '13.5px',
    fontWeight: '700',
    color: '#ffffff'
  },
  metricLabel: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '2px'
  },
  heroCtaWrap: {
    display: 'flex',
    gap: '14px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  heroPrimaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14.5px',
    fontWeight: '700',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.4)'
  },
  heroSecondaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    padding: '12px 22px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.2s ease'
  },
  contentContainer: {
    maxWidth: '1140px',
    margin: '0 auto',
    padding: '0 24px'
  },
  intakeBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#ecfdf5',
    border: '1px solid #a7f3d0',
    borderRadius: '12px',
    padding: '16px 20px',
    margin: '28px 0 36px 0',
    flexWrap: 'wrap',
    gap: '14px'
  },
  livePulseDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#10b981',
    boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.2)',
    flexShrink: 0
  },
  fastTrackPill: {
    background: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  sectionPill: {
    display: 'inline-block',
    background: '#f1f5f9',
    color: '#475569',
    borderRadius: '100px',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },
  sectionHeading: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#0f1e41',
    margin: '0 0 8px 0',
    letterSpacing: '-0.01em'
  },
  sectionSub: {
    fontSize: '15px',
    color: '#64748b',
    lineHeight: '1.6',
    margin: 0
  },
  filterTabsWrap: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    margin: '18px 0 28px 0'
  },
  filterTab: {
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '100px',
    padding: '7px 16px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  filterTabActive: {
    background: '#0f1e41',
    color: '#ffffff',
    border: '1px solid #0f1e41',
    borderRadius: '100px',
    padding: '7px 16px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  tracksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
    gap: '24px',
    marginBottom: '48px'
  },
  trackCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
  },
  trackCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px'
  },
  trackBadge: {
    fontSize: '11.5px',
    fontWeight: '700',
    borderRadius: '100px',
    padding: '4px 10px',
    border: '1px solid'
  },
  trackDuration: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500'
  },
  trackTitle: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#0f1e41',
    margin: '0 0 10px 0',
    lineHeight: '1.3'
  },
  trackDesc: {
    fontSize: '13.5px',
    color: '#475569',
    lineHeight: '1.6',
    margin: '0 0 14px 0',
    flexGrow: 1
  },
  trackSubheading: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#0f172a',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '6px'
  },
  skillsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '16px'
  },
  skillTag: {
    background: '#f1f5f9',
    color: '#334155',
    borderRadius: '6px',
    padding: '3px 8px',
    fontSize: '11.5px',
    fontWeight: '600'
  },
  deliverableBox: {
    background: '#f8fafc',
    border: '1px dashed #cbd5e1',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '18px'
  },
  trackApplyBtn: {
    background: '#0f1e41',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '13.5px',
    fontWeight: '700',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.2s ease'
  },
  formSection: {
    marginTop: '48px',
    scrollMarginTop: '100px'
  },
  formCard: {
    maxWidth: '820px',
    margin: '0 auto',
    background: '#ffffff',
    border: '1.5px solid #cbd5e1',
    borderRadius: '18px',
    padding: '32px',
    boxShadow: '0 10px 30px rgba(15, 30, 65, 0.08)'
  },
  formBannerTop: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '12px',
    padding: '14px 18px',
    marginBottom: '24px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '18px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  formLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '6px'
  },
  formInput: {
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#0f172a',
    outline: 'none',
    background: '#f8fafc'
  },
  formSelect: {
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13.5px',
    color: '#0f172a',
    outline: 'none',
    background: '#f8fafc'
  },
  paymentSectionBox: {
    background: '#f0fdf4',
    border: '1.5px solid #86efac',
    borderRadius: '14px',
    padding: '22px',
    marginTop: '24px'
  },
  govtApprovedBadge: {
    background: '#047857',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '800',
    borderRadius: '100px',
    padding: '4px 10px',
    letterSpacing: '0.04em'
  },
  deliverablesList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '8px',
    marginBottom: '16px',
    fontSize: '12.5px',
    color: '#065f46',
    fontWeight: '600'
  },
  delivItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  upiInfoCard: {
    background: '#ffffff',
    border: '1px solid #bbf7d0',
    borderRadius: '10px',
    padding: '14px'
  },
  upiPill: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12.5px',
    color: '#334155'
  },
  submitBtn: {
    background: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '800',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.4)',
    transition: 'all 0.2s ease'
  },
  submitBtnDisabled: {
    background: '#94a3b8',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '800',
    cursor: 'not-allowed'
  },
  successBox: {
    maxWidth: '720px',
    margin: '0 auto',
    background: '#ffffff',
    border: '2px solid #86efac',
    borderRadius: '18px',
    padding: '36px',
    textAlign: 'center',
    boxShadow: '0 10px 30px rgba(5, 150, 105, 0.1)'
  },
  appIdDisplay: {
    background: '#f8fafc',
    border: '1.5px dashed #cbd5e1',
    borderRadius: '12px',
    padding: '16px',
    display: 'inline-block',
    margin: '0 auto 24px auto',
    minWidth: '320px'
  },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  receiptCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px 20px',
    margin: '0 auto 24px auto',
    textAlign: 'left',
    maxWidth: '560px'
  },
  receiptRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '13.5px'
  },
  receiptLabel: {
    color: '#64748b',
    fontWeight: '500'
  },
  receiptVal: {
    color: '#0f172a',
    fontWeight: '700'
  },
  whatsappJoinBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#16a34a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 22px',
    fontSize: '14px',
    fontWeight: '700',
    textDecoration: 'none',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)'
  },
  printBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#0f1e41',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 22px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  faqCard: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    marginBottom: '10px',
    overflow: 'hidden'
  },
  faqHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left'
  },
  faqQuestion: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#0f1e41'
  },
  faqAnswer: {
    padding: '0 20px 18px 20px',
    fontSize: '14px',
    color: '#475569',
    lineHeight: '1.6',
    borderTop: '1px solid #f1f5f9',
    paddingTop: '12px'
  },
  helpdeskBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '14px',
    padding: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  helpdeskGFormBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  helpdeskEmailBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#ffffff',
    color: '#0f1e41',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    padding: '10px 18px',
    fontSize: '13px',
    fontWeight: '700',
    textDecoration: 'none'
  }
};

export default Internships;
